import { GroupAvatar, PersonAvatar } from "~/components/PersonAvatar";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";

export interface AvatarStackItem {
  /** A person's email or a group's name. Doubles as the avatar color seed. */
  id: string;
  name: string;
  imageUrl?: string | null;
  isGroup?: boolean;
}

interface AvatarStackProps {
  /** `undefined` while the caller's collaborator fetch is still in flight. */
  items: AvatarStackItem[] | undefined;
  max?: number;
  /** Shown when there is nobody. Omit to render nothing. */
  emptyLabel?: string;
}

/**
 * Overlapping avatars for the people and groups attached to something, each
 * named on hover, with everyone past `max` collapsed into a `+N` count.
 */
export function AvatarStack({ items, max = 5, emptyLabel }: AvatarStackProps) {
  if (items === undefined) {
    // Collaborators resolve after the row or tile they sit in; hold the slot.
    return (
      <span
        aria-hidden="true"
        className="inline-flex size-6 animate-pulse rounded-full bg-muted ring-2 ring-card"
      />
    );
  }
  if (items.length === 0) {
    return emptyLabel ? <span className="text-sm text-muted-foreground">{emptyLabel}</span> : null;
  }
  const overflowItems = items.slice(max);
  return (
    <div className="flex items-center gap-1">
      <div className="flex items-center">
        {items.slice(0, max).map((item, i) => (
          <Tooltip key={item.id}>
            <TooltipTrigger asChild>
              <span
                className={cn(
                  "relative inline-flex rounded-full ring-2 ring-card transition-transform duration-75 hover:z-10 hover:scale-110",
                  i > 0 && "-ml-1"
                )}
              >
                {item.isGroup ? (
                  <GroupAvatar />
                ) : (
                  <PersonAvatar name={item.name} email={item.id} imageUrl={item.imageUrl} />
                )}
              </span>
            </TooltipTrigger>
            <TooltipContent>{item.name}</TooltipContent>
          </Tooltip>
        ))}
      </div>
      {overflowItems.length > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="cursor-default text-xs text-muted-foreground">
              +{overflowItems.length}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <ul className="space-y-0.5">
              {overflowItems.map((item) => (
                <li key={item.id}>{item.name}</li>
              ))}
            </ul>
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}
